import { useState } from 'react';
import { api } from '../lib/api.js';

// Ali — MAPTRIUM's natural-language wizard assistant.
// Runs Sonnet 4.6, emits column mapping + symbology + section hints in
// one shot. Freehand hex arrays via custom_palette when the user asks
// for specific colors.
//
// Props:
//   columns        : array of {name, type, sample} from profile stage
//   projectType    : string, current preset
//   clientBrief    : string, from Step 1
//   jobId          : string, if a job draft has been created
//   onApply({ symbology, mapping, section_hints, rationale })
//                    parent merges into wizard state
//   onPreview({ symbology, mapping }) : optional, renders Ali's proposal
//                    directly without touching wizard state
export default function AliAssistPanel({ columns, projectType, clientBrief, jobId, onApply, onPreview }) {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const examples = [
    'Map my crash data. Address is in the street, city, state columns. Hotspots as red stars, legend by KABCO severity, top 10 in the report.',
    'Yellow stars, legend by district, top 10 hotspots. Label each pin with the intersection name.',
    'Fire stations from the address column. Label with station_name, metric is avg_response_min. Red triangles, bigger for slower.',
    'Traffic light colors on signal condition (good=green, fair=amber, poor=red). Signals are at the lat, lng columns.',
    'Diverging blue to red on turnout_pct, diamond markers. Precinct as the label. Legend title "Turnout %".',
  ];

  const generate = async () => {
    setError(null); setResult(null); setBusy(true);
    try {
      const res = await api.aliAssist({
        prompt: prompt.trim(),
        columns,
        project_type: projectType,
        client_brief: clientBrief,
        job_id: jobId,
      });
      setResult(res);
    } catch (e) {
      setError(e?.message || 'Ali could not generate right now.');
    } finally {
      setBusy(false);
    }
  };

  const apply = () => {
    if (!result) return;
    onApply?.({
      symbology: result.symbology,
      mapping: result.mapping,
      section_hints: result.section_hints,
      rationale: result.rationale,
    });
    setResult(r => r ? { ...r, _applied: true } : r);
  };

  const discard = () => { setResult(null); setError(null); };

  const previewAli = async () => {
    if (!result || !onPreview) return;
    setPreviewBusy(true);
    try {
      await onPreview({ symbology: result.symbology, mapping: result.mapping });
    } finally {
      setPreviewBusy(false);
    }
  };

  const noColumns = (columns || []).length === 0;

  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
      {/* Top strip: navy header with larger Ali portrait */}
      <div className="bg-navy text-white px-5 py-4 flex items-center gap-4">
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/10 border-2 border-gold/70 overflow-hidden flex-shrink-0 shadow-lg">
          <img src="/ali_avatar.png" alt="Ali" className="w-full h-full object-cover object-top" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-lg leading-tight">Ali</div>
          <div className="text-sm text-white/75 leading-tight">MAPTRIUM wizard assistant</div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Intro line */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full overflow-hidden border border-slate-200 flex-shrink-0 hidden sm:block">
            <img src="/ali_avatar.png" alt="" className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1 bg-slate-50 rounded-xl rounded-tl-sm px-4 py-3 text-sm text-ink">
            Hi. Tell me what you want the map to show. I&rsquo;ll configure column mapping, symbology, and hotspot settings for you. You can review and edit before saving.
          </div>
        </div>

        {/* Prompt input */}
        <div className="space-y-2">
          <label className="text-xs font-semibold uppercase tracking-widest text-ink-muted">Your request</label>
          <textarea
            className="input w-full h-24 text-sm resize-y"
            placeholder="e.g. Show top 10 crash hotspots. Legend by district. Yellow stars, bigger stars for more crashes."
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            maxLength={2000}
            disabled={busy}
          />
          <div className="flex flex-wrap items-center gap-3 text-xs text-ink-muted">
            <span>{prompt.length} / 2000</span>
            <span className="ml-auto">{noColumns ? 'Upload a file so Ali can see your columns.' : `${columns.length} columns visible to Ali`}</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !prompt.trim() || noColumns}
            onClick={generate}
          >
            {busy ? 'Ali is thinking…' : (result ? 'Regenerate' : 'Ask Ali')}
          </button>
          {result && (
            <>
              <button
                type="button"
                className="btn btn-gold"
                onClick={apply}
                title="Write Ali's picks into the wizard so Save and Preview use them"
              >
                {result._applied ? 'Re-apply' : 'Apply to wizard'}
              </button>
              {onPreview && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={previewAli}
                  disabled={previewBusy}
                  title="Preview Ali's proposal directly. Does not change wizard state."
                >
                  {previewBusy ? 'Rendering…' : 'Preview Ali’s map'}
                </button>
              )}
              <button type="button" className="btn btn-ghost" onClick={discard}>
                Discard
              </button>
            </>
          )}
          {result?._applied && (
            <span className="tag tag-green text-xs ml-auto">Applied. Preview and Save use these.</span>
          )}
          {result && !result._applied && (
            <span className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-1 ml-auto" title="Ali's picks are proposals only until you click Apply.">
              Not applied yet. Wizard fields below are unchanged.
            </span>
          )}
        </div>

        {/* Example prompts */}
        {!result && !busy && (
          <details className="text-xs">
            <summary className="cursor-pointer text-ink-muted hover:text-navy font-medium">Show 5 example prompts</summary>
            <ul className="mt-2 space-y-1">
              {examples.map((ex, i) => (
                <li key={i}>
                  <button
                    type="button"
                    className="text-left hover:underline text-navy w-full py-1"
                    onClick={() => setPrompt(ex)}
                  >
                    &ldquo;{ex}&rdquo;
                  </button>
                </li>
              ))}
            </ul>
          </details>
        )}

        {error && (
          <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-3">
            {error}
          </div>
        )}

        {/* Ali's response — chat-bubble style */}
        {result && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full overflow-hidden border border-gold/60 flex-shrink-0 hidden sm:block">
              <img src="/ali_avatar.png" alt="" className="w-full h-full object-cover object-top" />
            </div>
            <div className="flex-1 space-y-3 min-w-0">
              <div className="bg-gold/10 border border-gold/40 rounded-xl rounded-tl-sm px-4 py-3 text-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy/70 font-semibold mb-1">Ali says</div>
                <p className="text-ink">{result.rationale}</p>
              </div>

              {result.mapping && Object.keys(result.mapping).length > 0 && (
                <SummarySection title="Column mapping">
                  {result.mapping.location_kind && <SummaryCell label="Location kind" value={result.mapping.location_kind} />}
                  {result.mapping.lat_col && <SummaryCell label="Latitude" value={result.mapping.lat_col} />}
                  {result.mapping.lng_col && <SummaryCell label="Longitude" value={result.mapping.lng_col} />}
                  {result.mapping.address_col && <SummaryCell label="Address" value={result.mapping.address_col} />}
                  {result.mapping.street_col && <SummaryCell label="Street" value={result.mapping.street_col} />}
                  {result.mapping.city_col && <SummaryCell label="City" value={result.mapping.city_col} />}
                  {result.mapping.state_col && <SummaryCell label="State" value={result.mapping.state_col} />}
                  {result.mapping.zip_col && <SummaryCell label="ZIP" value={result.mapping.zip_col} />}
                  {result.mapping.label_col && <SummaryCell label="Label" value={result.mapping.label_col} />}
                  {result.mapping.value_col && <SummaryCell label="Value" value={result.mapping.value_col} />}
                  {result.mapping.category_col && <SummaryCell label="Category" value={result.mapping.category_col} />}
                  {result.mapping.hotspot_columns?.length > 0 && <SummaryCell label="Hotspot cols" value={result.mapping.hotspot_columns.join(', ')} />}
                </SummarySection>
              )}

              <SummarySection title="Symbology">
                <SummaryCell label="Palette" value={result.symbology?.custom_palette?.length ? `Custom (${result.symbology.custom_palette.length} colors)` : result.symbology?.palette} />
                <SummaryCell label="Shape" value={result.symbology?.shape} />
                <SummaryCell label="Icon" value={result.symbology?.icon || 'none'} />
                <SummaryCell label="Color by" value={result.symbology?.color_by || 'single color'} />
                {result.symbology?.size_by && <SummaryCell label="Size by" value={result.symbology.size_by} />}
                {result.symbology?.classify && result.symbology.color_by && <SummaryCell label="Classify" value={result.symbology.classify} />}
                {result.symbology?.legend_title && <SummaryCell label="Legend title" value={`"${result.symbology.legend_title}"`} />}
                <SummaryCell label="Model" value={result.model_used} />
              </SummarySection>

              {result.symbology?.custom_palette?.length > 0 && (
                <div className="flex items-center gap-2 px-2">
                  <span className="text-xs text-ink-muted">Custom palette:</span>
                  <div className="flex gap-1">
                    {result.symbology.custom_palette.map((h, i) => (
                      <span key={i} title={h} className="w-6 h-6 rounded border border-slate-300 shadow-sm" style={{ backgroundColor: h }} />
                    ))}
                  </div>
                </div>
              )}

              {result.section_hints && Object.keys(result.section_hints).length > 0 && (
                <SummarySection title="Report hints">
                  {result.section_hints.hotspots?.top_n && <SummaryCell label="Top N hotspots" value={result.section_hints.hotspots.top_n} />}
                  {result.section_hints.hotspots?.sort_dir && <SummaryCell label="Sort" value={result.section_hints.hotspots.sort_dir} />}
                  {result.section_hints.stats?.primary_metric && <SummaryCell label="Stats metric" value={result.section_hints.stats.primary_metric} />}
                  {result.section_hints.jurisdiction?.group_by && <SummaryCell label="Group by" value={result.section_hints.jurisdiction.group_by} />}
                </SummarySection>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummarySection({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-widest text-ink-muted font-semibold mb-2">{title}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">{children}</div>
    </div>
  );
}

function SummaryCell({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-widest text-ink-muted">{label}</div>
      <div className="font-mono text-xs text-navy truncate" title={String(value)}>{String(value ?? '')}</div>
    </div>
  );
}
